import { Dispatch } from '@reduxjs/toolkit';
import { AnyAction } from '@reduxjs/toolkit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { PageLink } from '@/components/PageLink';
import { ActionTags } from '@/components/ActionTags';
import { ClientActionMenu } from '@/components/ClientActionMenu';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { SelectReferralSource } from '@/components/SelectReferralSource';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { SelectClientRelationshipStage } from '@/components/SelectClientRelationshipStage';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OnboardingOverview } from '@/pages/OnboardingOverview';

import { MdOutlineFilterList } from 'react-icons/md';
import { InfoIcon, UserIcon, MailIcon, Shield, Send } from 'lucide-react';

import { useNavigateToPage } from '@client';
import { useNavigate } from 'react-router-dom';
import React, { useCallback, useMemo, useEffect, useState } from 'react';

import _, { isUndefined } from 'lodash';
import { createColumnHelper, Table } from '@tanstack/react-table';
import { findClientPartner, fullName as sharedFullName, isPlaceholderEmail } from '@shared';
import { putClientReferralSourceByClientIdRequest } from '@/model/clientModel';
import { renderHeaderCell, renderTableCell, SortHeader } from '@/components/DataTableColumn';

import {
  cn,
  ERROR_OR_INCOMPLETE_STATUS_TAGS,
  INTAKE_FORM_INCOMPLETE,
  type ModalState,
} from '@client';
import { useSendReminderStore } from '@/features/send-reminder';

const columnHelper = createColumnHelper<Client>();

type ClientTableColumnsProps = {
  data: Client[];
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
  dispatch: Dispatch<AnyAction>;
};

interface FilterButtonProps<T> {
  values: T[];
  selectedValues: T[];
  setSelectedValues: (values: T[]) => void;
  renderValue?: (value: T) => string;
  keySelector?: (value: T) => string;
}

function FilterButton<T>({
  values,
  selectedValues,
  setSelectedValues,
  renderValue = value => String(value),
  keySelector = value => String(value),
}: FilterButtonProps<T>) {
  const isValueSelected = (value: T) => {
    if (value === 'Undefined') {
      // If the value is 'Undefined', check if any of the selected values are undefined or null
      return selectedValues.some(v => v === undefined || v === null);
    }
    return selectedValues.includes(value);
  };

  const hasActiveFilters = selectedValues.length > 0;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'text-primary-dark hover:bg-primary-100',
            hasActiveFilters && 'bg-primary/20 ml-0.5',
          )}
        >
          <MdOutlineFilterList className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-96 overflow-y-scroll rounded-xl p-2"
      >
        {values.map(value => (
          <DropdownMenuCheckboxItem
            key={keySelector ? keySelector(value) : renderValue(value)}
            className="capitalize"
            checked={isValueSelected(value)}
            onCheckedChange={checked => {
              setSelectedValues(
                checked
                  ? [...selectedValues, value]
                  : selectedValues.filter(v =>
                      // If the selected filter value is 'Undefined', remove undefined values,
                      // otherwise remove the exact value
                      value === 'Undefined' ? !isUndefined(v) : v !== value,
                    ),
              );
            }}
          >
            {renderValue(value)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function StatusHeader({ table }: { table: Table<Client> }) {
  const filterValue = table.getColumn('statuses')?.getFilterValue() as string[];
  const [selectedTags, setSelectedTags] = useState<string[]>(filterValue ?? []);

  const allTags = _.sortBy(
    Array.from(
      new Set(table.getPreFilteredRowModel().rows.flatMap(row => row.original.clientTags)),
    ),
    tag => (tag ? tag.toLowerCase() : ''),
  );

  const handleFilterChange = (value: string[]) => {
    setSelectedTags(value);
    table.getColumn('statuses')?.setFilterValue(value);
  };

  return (
    <div className="flex items-center">
      {renderHeaderCell(
        'Statuses',
        'ml-2',
        "Status tags show the client's current state and action items",
      )}
      <FilterButton
        values={allTags}
        selectedValues={selectedTags}
        setSelectedValues={handleFilterChange}
        renderValue={value => value?.toString() ?? 'None'}
      />
    </div>
  );
}

export function RelationshipStageHeader({ table }: { table: Table<Client> }) {
  const filterValue = table.getColumn('relationshipStage')?.getFilterValue() as (
    | ClientRelationshipStage
    | undefined
  )[];

  const [selectedStages, setSelectedStages] = useState<(ClientRelationshipStage | undefined)[]>(
    filterValue ?? [],
  );

  const allStages = _.sortBy(
    Array.from(
      new Set(table.getPreFilteredRowModel().rows.map(row => row.original.clientRelationshipStage)),
    ),
    stage => stage?.toString().toLowerCase() ?? '',
  );

  const handleFilterChange = (value: (ClientRelationshipStage | undefined)[]) => {
    setSelectedStages(value);
    table.getColumn('relationshipStage')?.setFilterValue(value);
  };

  return (
    <div className="flex items-center">
      {renderHeaderCell(
        'Relationship Stage',
        undefined,
        'The current stage of your relationship with this client',
      )}
      <FilterButton
        values={allStages}
        selectedValues={selectedStages}
        setSelectedValues={handleFilterChange}
        renderValue={value => value?.toString() ?? 'Undefined'}
      />
    </div>
  );
}

export function OrganizationHeader({ table }: { table: Table<Client> }) {
  const filterValue = table.getColumn('organization')?.getFilterValue() as (string | undefined)[];
  const [selectedOrgs, setSelectedOrgs] = useState<(string | undefined)[]>(filterValue ?? []);

  const allOrgs = _.sortBy(
    Array.from(
      new Set(
        table
          .getPreFilteredRowModel()
          .rows.map(row => row.getValue<string | undefined>('organization')),
      ),
    ),
    org => org?.toString().toLowerCase().trim() ?? '',
  );

  useEffect(() => {
    table.getColumn('organization')?.setFilterValue(selectedOrgs);
  }, [selectedOrgs, table]);

  return (
    <div className="flex items-center gap-2">
      {renderHeaderCell(
        'Organization',
        undefined,
        "The organization the client's advisor belongs to",
      )}
      <FilterButton
        values={allOrgs}
        selectedValues={selectedOrgs}
        setSelectedValues={setSelectedOrgs}
        renderValue={value => value?.toString().trim() ?? ''}
      />
    </div>
  );
}

export function ReferralSourceHeader({
  table,
  subordinateAdvisors,
  sessionAdvisor,
}: {
  table: Table<Client>;
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
}) {
  const filterValue = table.getColumn('referralSource')?.getFilterValue() as (string | undefined)[];
  const [selectedSources, setSelectedSources] = useState<(string | undefined)[]>(filterValue ?? []);

  const allSources = useMemo(
    () =>
      Array.from(
        new Set(
          table.getPreFilteredRowModel().rows.map(row => {
            const advisor = getClientAdvisor(row.original, sessionAdvisor, subordinateAdvisors);
            const referralSourceId = row.original.clientReferralSourceId;
            const referralSource = advisor?.referralSources?.find(
              source => source.advisorReferralSourceId === referralSourceId,
            );
            return {
              id: referralSourceId,
              label: referralSource?.referralSourceLabel,
            };
          }),
        ),
      ),
    [table.getPreFilteredRowModel().rows, sessionAdvisor, subordinateAdvisors],
  );

  const handleFilterChange = (value: (string | undefined)[]) => {
    // Get all sources that share labels with the selected sources
    const selectedLabels = new Set(value.map(v => renderValue(v)));
    const relatedSources = Array.from(
      new Set(
        allSources
          .filter(source => selectedLabels.has(renderValue(source.id)))
          .map(source => source.id),
      ),
    );

    setSelectedSources(relatedSources);
    table.getColumn('referralSource')?.setFilterValue(relatedSources);
  };

  const renderValue = (value: string | undefined) => {
    const source = allSources.find(source => source.id === value);
    return source?.label ?? 'Undefined';
  };

  // Get unique sources by label. Necessary for is supervisor/subordinate advisor have the same referral source.
  const uniqueSourceIdsByLabel = useMemo(() => {
    const labelMap = new Map<string, string>();
    allSources.forEach(source => {
      const label = renderValue(source.id);
      if (!labelMap.has(label)) {
        labelMap.set(label, source.id ?? 'Undefined');
      }
    });
    return _.sortBy(Array.from(labelMap.values()), id =>
      id === 'Undefined' ? '' : renderValue(id).toLowerCase().trim(),
    );
  }, [allSources]);

  return (
    <div className="flex items-center">
      {renderHeaderCell('Referral Source', undefined, 'How this client was referred to you')}
      <FilterButton
        values={uniqueSourceIdsByLabel}
        selectedValues={selectedSources}
        setSelectedValues={handleFilterChange}
        renderValue={renderValue}
        keySelector={value => value?.toString() ?? 'Undefined'}
      />
    </div>
  );
}

export const createClientTableColumns = ({
  data,
  setModalState,
  sessionAdvisor,
  subordinateAdvisors,
  dispatch,
}: ClientTableColumnsProps) => {
  return [
    columnHelper.accessor(row => `${row.clientFirstName} ${row.clientLastName}`, {
      id: 'clientName',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Client Name"
          />,
          'ml-4',
          "Client's full name and available actions",
        ),
      cell: info =>
        renderTableCell(
          <ClientName
            client={info.row.original}
            sessionAdvisor={sessionAdvisor}
            setModalState={setModalState}
          />,
          'pr-0',
        ),
      enableHiding: false,
      enablePinning: true,
      size: 220,
    }),
    columnHelper.accessor(
      row => getAdvisorOrganization(row.advisorId, subordinateAdvisors, sessionAdvisor),
      {
        id: 'organization',
        header: info => <OrganizationHeader table={info.table} />,
        cell: info => {
          const client = info.row.original;
          const organization = info.getValue();

          // Only look up advisor if client doesn't belong to session advisor
          if (client.advisorId === sessionAdvisor.advisorId) {
            return renderTableCell(organization);
          }

          const advisor = subordinateAdvisors.find(a => a.advisorId === client.advisorId);
          if (!advisor) {
            return renderTableCell(organization);
          }

          return renderTableCell(
            <HoverCard>
              <HoverCardTrigger asChild>
                <span className="flex items-center gap-1">
                  <span className="text-gray-60">{organization}</span>
                  <InfoIcon className="text-primary-500 h-4 w-4 flex-shrink-0" />
                </span>
              </HoverCardTrigger>
              <HoverCardContent className="w-auto max-w-80 p-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Advisor Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <UserIcon className="text-primary-dark h-4 w-4" />
                      <span className="font-normal">
                        {`${advisor.advisorFirstName || ''} ${advisor.advisorLastName || ''}`.trim()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MailIcon className="text-primary-dark h-4 w-4" />
                      <span className="font-normal">{advisor.advisorEmail}</span>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>,
          );
        },
        enableHiding: true,
        filterFn: (row, _columnId, filterValue: (string | undefined)[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          return filterValue.includes(row.getValue('organization'));
        },
      },
    ),
    columnHelper.accessor(row => clientPartnerFullName(row, data), {
      id: 'partner',
      header: () =>
        renderHeaderCell('Partner', undefined, "The client's partner or spouse if applicable"),
      cell: info => renderTableCell(info.getValue()),
    }),

    columnHelper.display({
      id: 'statuses',
      header: info => <StatusHeader table={info.table} />,
      cell: ({ row }) => {
        const client = row.original;
        return (
          <ActionTags
            clientTags={client.clientTags}
            client={client}
          />
        );
      },
      filterFn: (row, _columnId, filterValue: string[]) => {
        // If no tags are selected, show all rows
        if (!filterValue || filterValue.length === 0) return true;

        return filterValue.some(tag => row.original.clientTags.includes(tag));
      },
    }),
    columnHelper.accessor(row => row.clientRelationshipStage, {
      id: 'relationshipStage',
      header: info => <RelationshipStageHeader table={info.table} />,
      cell: info =>
        renderTableCell(
          <SelectClientRelationshipStage
            clientRelationshipStage={info.getValue()}
            client={info.row.original}
            triggerClassName="h-6 w-[138px] rounded-full bg-white"
          />,
        ),
      filterFn: (row, _columnId, filterValue: (ClientRelationshipStage | undefined)[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(row.original.clientRelationshipStage);
      },
    }),
    columnHelper.accessor('clientEmail', {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Email"
          />,
          undefined,
          "Client's email address",
        ),
      cell: info => renderTableCell(isPlaceholderEmail(info.getValue()) ? '' : info.getValue()),
    }),
    columnHelper.accessor(row => row.clientAddedDateTime, {
      id: 'addedDate',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            children="Added Date"
          />,
          undefined,
          'Date when the client was added to the system',
        ),
      cell: info => {
        const date = info.getValue();
        return renderTableCell(
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{date.toLocaleDateString()}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{date.toLocaleTimeString()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>,
          'ml-2',
        );
      },
    }),
    columnHelper.accessor(row => row.clientReferralSourceId, {
      id: 'referralSource',
      header: info => (
        <ReferralSourceHeader
          table={info.table}
          sessionAdvisor={sessionAdvisor}
          subordinateAdvisors={subordinateAdvisors}
        />
      ),
      cell: info =>
        renderTableCell(
          <SelectReferralSource
            selectedReferralSourceId={info.getValue()}
            addInlineOption={true}
            includeFieldLabel={false}
            placeholderText="Undefined"
            triggerClassName="h-6 w-[138px] rounded-full bg-white"
            selectValueClassName="text-gray-700"
            disabled={info.row.original.advisorId !== sessionAdvisor.advisorId}
            onChangeReferralSourceId={value => {
              dispatch(
                putClientReferralSourceByClientIdRequest({
                  clientId: info.row.original.clientId,
                  advisorReferralSourceId: value,
                  advisorId: sessionAdvisor.advisorId,
                }),
              );
            }}
            advisor={getClientAdvisor(info.row.original, sessionAdvisor, subordinateAdvisors)}
            client={info.row.original}
          />,
        ),
      filterFn: (row, _columnId, filterValue: (string | undefined)[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(row.original.clientReferralSourceId);
      },
    }),
  ];
};

function ClientName({
  client,
  sessionAdvisor,
  setModalState,
}: {
  client: Client;
  sessionAdvisor: Advisor;
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}) {
  const clientTags = client.clientTags;
  const navigateToPage = useNavigateToPage();
  const navigate = useNavigate();
  const canViewResults = clientCanViewResults(clientTags);
  const { openWithClient } = useSendReminderStore();

  const handleExploreQuotes = useCallback(() => {
    navigate(`/clients/${client.clientId}/onboarding/shop-insurance`);
  }, [navigate, client.clientId]);

  const handleSendReminder = useCallback(() => {
    openWithClient(client);
  }, [openWithClient, client]);

  const handleNavigateToClient = useCallback(() => {
    navigateToPage(OnboardingOverview, { clientId: client.clientId });
  }, [navigateToPage, client.clientId]);

  const clientNameButton = (
    <Button
      variant="ghost"
      className="text-primary-dark hover:bg-primary-100 font-semibold"
      disabled={!canViewResults}
    >
      {sharedFullName(client)}
    </Button>
  );

  const isDifferentAdvisor = sessionAdvisor.advisorId !== client.advisorId;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-col items-start gap-1">
        {!canViewResults ? (
          clientNameButton
        ) : isDifferentAdvisor ? (
          <AdvisorWarningDialog
            title="Viewing another advisor's client"
            message="Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
            onContinue={handleNavigateToClient}
          >
            {clientNameButton}
          </AdvisorWarningDialog>
        ) : (
          <PageLink
            to={OnboardingOverview}
            targetProps={{ clientId: client.clientId }}
          >
            {clientNameButton}
          </PageLink>
        )}
        {canViewResults && client.isQuoteAvailable && (
          <Button
            variant="ghost"
            size="sm"
            className="border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 h-7 w-fit self-start border px-2 text-xs font-medium transition-opacity md:opacity-0 md:group-hover:opacity-100"
            onClick={handleExploreQuotes}
            title={`Explore quotes for ${sharedFullName(client)}`}
          >
            <Shield className="h-3.5 w-3.5" />
            <span className="ml-1">Explore Quotes</span>
          </Button>
        )}
        {clientTags.includes(INTAKE_FORM_INCOMPLETE) && (
          <Button
            variant="ghost"
            size="sm"
            className="border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 h-7 w-fit self-start border px-2 text-xs font-medium transition-opacity md:opacity-0 md:group-hover:opacity-100"
            onClick={handleSendReminder}
            title={`Send reminder to ${sharedFullName(client)}`}
          >
            <Send className="h-3.5 w-3.5" />
            <span className="ml-1">Send Reminder</span>
          </Button>
        )}
      </div>
      <div className="opacity-0 transition-opacity group-hover:opacity-100">
        <ClientActionMenu
          client={client}
          sessionAdvisor={sessionAdvisor}
          setModalState={setModalState}
        />
      </div>
    </div>
  );
}

export function clientCanViewResults(clientTags: string[]) {
  return !clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag));
}

export function clientPartnerFullName(client: Client, fullClientList: Client[]) {
  const partner = findClientPartner(client, fullClientList);
  return partner ? sharedFullName(partner) : '';
}

export function getAdvisorOrganization(
  advisorId: string,
  subordinateAdvisors: Advisor[],
  sessionAdvisor: Advisor,
): string {
  if (advisorId === sessionAdvisor.advisorId) {
    return sessionAdvisor.organizationDisplayName || sessionAdvisor.organizationName || '';
  }
  const advisor = subordinateAdvisors.find(a => a.advisorId === advisorId);
  return advisor?.organizationDisplayName || advisor?.organizationName || '';
}

export function getClientAdvisor(
  client: Client,
  sessionAdvisor: Advisor,
  subordinateAdvisors: Advisor[],
) {
  if (client.advisorId === sessionAdvisor.advisorId) {
    return sessionAdvisor;
  }
  const advisor = subordinateAdvisors.find(a => a.advisorId === client.advisorId);
  return advisor;
}

// Helper function to get a subordinate advisor by ID
export function getSubordinateAdvisor(
  advisorId: string,
  subordinateAdvisors: Advisor[],
): Advisor | undefined {
  return subordinateAdvisors.find(a => a.advisorId === advisorId);
}

// Helper function to get full name for an advisor
export function advisorFullName(advisor: Advisor): string {
  return `${advisor.advisorFirstName || ''} ${advisor.advisorLastName || ''}`.trim();
}
